'use client';

import { ReactNode, useEffect } from 'react';
import { AppShell } from '@/new_components/app-shell/AppShell';
import { useLocale } from '@/hooks/use-locale';
import { useAppShell } from '@/providers/app-shell-provider';
import { useSettings } from '@/providers/settings-provider';
import { Footer } from './components/footer';
import { Header } from './components/header';

/** Internal pathnames where content goes full-width (no max-width or border lines).
 * Compared against the canonical (delocalized) path so both /de/suche and /en/search
 * resolve to /search. */
const FULL_WIDTH_ROUTES = ['/search', '/plans', '/overview', '/admin'];

/** Internal pathnames that render a full-viewport pane (Scalar docs) and should suppress the footer. */
const NO_FOOTER_ROUTES = ['/developer/apis'];

const matchesRoute = (pathname: string, route: string) =>
  pathname === route ||
  pathname.startsWith(route + '?') ||
  pathname.startsWith(route + '/');

const Demo7Layout = ({
  children,
  hideGridLines = false,
}: {
  children: ReactNode;
  hideGridLines?: boolean;
}) => {
  const { signedInShell } = useAppShell();
  const { setOption } = useSettings();
  const { internalPathname } = useLocale();
  const isFullWidth =
    hideGridLines ||
    FULL_WIDTH_ROUTES.some((r) => matchesRoute(internalPathname, r));
  const hideFooter = NO_FOOTER_ROUTES.some((r) =>
    matchesRoute(internalPathname, r),
  );

  useEffect(() => {
    setOption('layout', 'demo7');
  }, [setOption]);

  if (signedInShell)
    return (
      <AppShell isFullWidth={isFullWidth} hideFooter={hideFooter}>
        {children}
      </AppShell>
    );

  return (
    <div className="flex grow flex-col min-w-0 mx-auto w-full">
      <Header />

      <div
        className={`flex grow flex-col min-w-0 mx-auto w-full${isFullWidth ? '' : ' max-w-[1320px] border-x border-border'}`}
      >
        <main className="grow min-w-0 min-h-[calc(100vh-var(--header-height,64px))] lg:min-h-0">
          {children}
        </main>
      </div>

      {!hideFooter && <Footer />}
    </div>
  );
};

export { Demo7Layout };
export default Demo7Layout;
